import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { Popover, Whisper } from "rsuite";
import { Button } from "reactstrap";

import AppEmptyState from "../../../elements/AppEmptyState";
import AppDrawer from "../AppDrawer";
import AppSpinner from "../../../elements/AppSpinner";
import useFetchComments from "../../../api/api-hooks/comments/useFetchComments";
import AppComment from "./AppComment";
import CommentsEditor from "./CommentsEditor";
import useCommentReaction from "../../../api/api-hooks/comments/useCommentReaction";
import {
  ConvoMetadata,
  RecordComment,
} from "../../../api/api-hooks/comments/useCreateComment";
import usePrevious from "../../../hooks/usePrevious";
import useDeleteComment from "../../../api/api-hooks/comments/useDeleteComment";
import IconButton from "../../../elements/IconButton";
import PopoverDropdownMenu from "../../Menus/PopoverDropdownMenu";
import AppToggle from "../../Inputs/AppToggle";
import { useUpdateNotifications } from "../../../api/api-hooks/comments/useUpdateNotifications";
import BrieChatWithGCHeader from "./BrieChatWithGC/BrieChatWithGCHeader";
import BrieChatWithGC from "./BrieChatWithGC/BrieChatWithGC";

interface Props {
  open: boolean;
  activeTab?: string;
  onClose: () => void;
  context: string;
  contextID: string | undefined;
  convoMetadata: ConvoMetadata;
  inline?: boolean;
  recordChat?: boolean;
}

export default function CommentsDrawer({
  open,
  activeTab,
  onClose,
  context,
  contextID,
  convoMetadata,
  inline = false,
  recordChat = false,
}: Props) {
  const { commentPages, loading, loadingNext, hasMore, fetchNextPage } =
    useFetchComments(context, contextID, !open);
  const { updateNotifcations, updatingNotifications } =
    useUpdateNotifications();
  const { reactToComment } = useCommentReaction();
  const { deleteComment } = useDeleteComment();
  const [replyingTo, setReplyingTo] = useState<RecordComment | null>(null);
  const [editComment, setEditComment] = useState<RecordComment | null>(null);
  const previousPagesCount = usePrevious(commentPages.length, []);
  const [subscriber, setSubscriber] = useState(false);

  const settingsCommentsRef = useRef<any>(null);
  const newestCommentRef = useRef<HTMLDivElement>(null);
  const isFirstRender = useRef(true);
  const fetchNextRef = useRef<any>();

  fetchNextRef.current = fetchNextPage;
  useEffect(() => {
    setSubscriber(commentPages?.[0]?.isASubscriber ?? false);
  }, [commentPages?.[0]?.isASubscriber]);

  const oldestCommentObserver = useRef<any>(null);
  const oldestCommentRef: any = useCallback(
    (commentNode: any) => {
      if (loading) return;

      if (oldestCommentObserver.current)
        oldestCommentObserver.current.disconnect();

      oldestCommentObserver.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && !isFirstRender.current && hasMore) {
          fetchNextPage();
        }
      });

      if (commentNode) oldestCommentObserver.current.observe(commentNode);
    },
    [loading, hasMore, fetchNextPage]
  );

  const usersMap = commentPages?.[0]?.users || {};

  // Scroll to most recent message when new messages are added.
  useLayoutEffect(() => {
    if (!newestCommentRef.current?.scrollIntoView) return;

    if (isFirstRender.current) {
      // @ts-ignore
      newestCommentRef.current?.scrollIntoView({
        behavior: "instant",
        block: "end",
        inline: "nearest",
      });
      setTimeout(() => (isFirstRender.current = false));
    } else {
      newestCommentRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "end",
        inline: "nearest",
      });
    }
  }, [commentPages?.[0]?.totalComments, open]);

  // Fixes bug where you lose your scroll position after loading new Comments
  useLayoutEffect(() => {
    const lastPageIndex = commentPages?.length - 2;

    if (lastPageIndex < 0) return;

    // Ensure we are only do this when the number of pages INCREASES
    if (
      previousPagesCount.current !== undefined &&
      previousPagesCount.current <= commentPages?.length
    )
      return;

    const lastPageComments = commentPages?.[lastPageIndex]?.comments;

    if (!Array.isArray(lastPageComments)) return;

    const lastPageComment = lastPageComments[lastPageComments.length - 1];

    document
      .getElementById(lastPageComment._id!)
      // @ts-ignore
      ?.scrollIntoView({
        behavior: "instant",
        block: "end",
        inline: "nearest",
      });
  }, [commentPages?.length]);

  const CommentsSection = (
    <div className="tw:flex tw:flex-col tw:h-full">
      <div
        className={`tw:flex tw:justify-between tw:items-center border-bottom tw:py-2 tw:px-2 tw:w-full`}
      >
        <div className="tw:flex tw:items-center">
          <IconButton
            icon={inline ? "chevron-right" : "close"}
            onClick={handleClose}
          />
          {recordChat ? (
            <BrieChatWithGCHeader
              tabs={[{ title: "AI Chat", activeValue: "BrieGPT" }]}
            />
          ) : (
            <h3 className="mb-0">Comments</h3>
          )}
        </div>
        <div className="">
          {!!commentPages?.[0]?.totalComments && (
            <Whisper
              preventOverflow
              placement="autoVerticalEnd"
              trigger="click"
              ref={settingsCommentsRef}
              speaker={
                <Popover
                  className="sidebar-popover"
                  full
                  onClick={(e: any) => e.stopPropagation()}
                >
                  <PopoverDropdownMenu>
                    <h5 className="px-2 pt-2 text-dark">
                      Notification Settings
                    </h5>
                    <hr className="mx--1 mt-2 mb-3" />
                    <AppToggle
                      className="mb-2"
                      title="Subscribe to Thread"
                      onToggle={() => {
                        if (!contextID) return;

                        updateNotifcations({
                          context,
                          contextID,
                          addSubscriber: !subscriber,
                          onSuccess: () => setSubscriber(!subscriber),
                        });
                      }}
                      value={subscriber}
                      disabled={updatingNotifications}
                    />
                  </PopoverDropdownMenu>
                </Popover>
              }
              controlId="newCommentDropdown"
            >
              <Button color="secondary" outline className="btn-icon-only">
                <i className="mdi mdi-bell cursor-pointer" />
              </Button>
            </Whisper>
          )}
        </div>
      </div>
      {activeTab === "BrieGPT" && recordChat ? (
        <BrieChatWithGC
          recordID={contextID}
          moduleAPIName={convoMetadata?.RecordModule}
          recordChat={true}
        />
      ) : (
        <>
          <div
            className="tw:h-full overflow-auto px-3 px-md-4"
            style={{ flex: 1 }}
          >
            {commentPages?.length > 0 &&
            commentPages[0]?.comments?.length > 0 ? (
              <>
                <div className="d-flex flex-column-reverse">
                  {commentPages.map((page, i) => {
                    return page?.comments?.map((comment, j) => {
                      const isOldest =
                        i === commentPages?.length - 1 &&
                        j === page.comments.length - 1;
                      const isNewest = i === 0 && j === 0;

                      if (!comment || comment.Context !== "Record") return null;

                      return (
                        <AppComment
                          comment={comment}
                          commentRef={
                            isOldest
                              ? oldestCommentRef
                              : isNewest
                              ? newestCommentRef
                              : undefined
                          }
                          usersMap={usersMap}
                          key={comment._id}
                          handleReaction={handleReaction(comment._id!)}
                          handleEdit={handleEdit(comment)}
                          handleDelete={handleDelete(comment._id!)}
                          replyingTo={replyingTo as RecordComment}
                          setReplyingTo={
                            setReplyingTo as React.Dispatch<
                              React.SetStateAction<RecordComment | null>
                            >
                          }
                          editComment={editComment as RecordComment}
                          setEditComment={
                            setEditComment as React.Dispatch<
                              React.SetStateAction<RecordComment | null>
                            >
                          }
                        />
                      );
                    });
                  })}
                  {loadingNext && hasMore && (
                    <div className="tw:flex tw:justify-center my-2">
                      <AppSpinner />
                    </div>
                  )}
                </div>
              </>
            ) : loading ? (
              <div className="tw:flex tw;flex-col tw:h-full tw:w-full tw:justify-center tw:items-center">
                <AppSpinner size="md" />
              </div>
            ) : (
              <AppEmptyState
                className="mt-4"
                hideIcon
                title="Start a Conversation"
                description="There are no comments to show. Add your first comment to start a conversation with your team regarding this item"
              />
            )}
          </div>
          <CommentsEditor
            context={context}
            contextID={contextID}
            convoMetadata={convoMetadata}
            commentCount={commentPages?.[0]?.totalComments || 0}
            replyingTo={replyingTo}
            setReplyingTo={setReplyingTo}
            editComment={editComment}
            setEditComment={setEditComment}
            usersMap={usersMap}
          />
        </>
      )}
    </div>
  );

  if (inline && open) return CommentsSection;

  return (
    <AppDrawer
      open={open}
      onClose={handleClose}
      size="sm"
      bodyCSS={`pt-0 ${activeTab === "BrieGPT" ? "pb-0" : ""}`}
      disablePadding
      body={CommentsSection}
      headerTitle={undefined}
      disableHeader
    />
  );

  function handleClose() {
    onClose();
    isFirstRender.current = true;
  }

  function handleReaction(commentID: string) {
    if (!contextID) return;

    return (reaction: string) =>
      reactToComment({ context, contextID, commentID, reaction });
  }

  function handleEdit(comment: RecordComment) {
    return () => {
      setReplyingTo(null);
      setEditComment(comment);
    };
  }

  function handleDelete(commentID: string) {
    if (!contextID) return;

    return () => deleteComment({ commentID, context, contextID });
  }
}
